// src/pages/EmailTemplates.jsx
// Control Panel → Email Templates (Super Admins only). Every email the fax
// system sends: when it goes out, to whom, from which address and which part
// of the system, with a preview from the real code and a test copy to you.
// Most can be edited (3.7.0; the gateway scripts' notices from 3.8.0):
// subject, heading, body in rich text, button and small print, with
// {placeholders} filled in when the email is sent, a live preview, history,
// and "back to the original". Announcements (3.8.0): Announcements.jsx.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { apiCall, fmtWhen, Preview, RichText, PlaceholderPicker, usePlaceholderTarget, EDITOR_CSS } from '../components/EmailEditorParts';
import Announcements from './Announcements';
import { PILL, PILL_ON } from '../theme/tabs';
import Loader from '../components/Loader';
import Emo, { emoLabel } from '../components/Emo';

const call = (path, getToken, options) => apiCall(`/portal/email-templates${path}`, getToken, options);
const FIELDS = ['subject', 'heading', 'body', 'button', 'smallPrint'];
const same = (a, b) => FIELDS.every(f => (a?.[f] || '') === (b?.[f] || ''));

export default function EmailTemplates() {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [list, setList]     = useState(null);
  const [error, setError]   = useState('');
  const [q, setQ]           = useState('');
  const [picked, setPicked] = useState(null);     // template id
  const [variant, setVariant] = useState(null);
  const [preview, setPreview] = useState(null);   // { subject, html } | { error }
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [mode, setMode]       = useState('system');   // 'system' | 'announcements'

  const loadList = useCallback(() => call('', getToken).then(r => { setList(r.data); return r.data; }), [getToken]);
  useEffect(() => {
    loadList().then(data => setPicked(p => p || data[0]?.id || null)).catch(e => setError(e.message));
  }, [loadList]);

  const current = useMemo(() => (list || []).find(t => t.id === picked) || null, [list, picked]);
  useEffect(() => { setVariant(v => (current?.variants?.some(x => x.id === v) ? v : current?.variants?.[0]?.id || null)); }, [current]);
  const currentVariant = current?.variants?.find(v => v.id === variant) || null;

  const loadPreview = useCallback(async () => {
    setPreview(null);
    if (!current?.hasPreview) return;
    try { setPreview(await call(`/${encodeURIComponent(current.id)}/preview?variant=${encodeURIComponent(variant || '')}`, getToken)); }
    catch (e) { setPreview({ error: e.message }); }
  }, [current, variant, getToken]);
  useEffect(() => { if (!editing) loadPreview(); }, [loadPreview, editing]);

  // Leaving an unsaved edit asks first (3.67.7: the portal's own dialog)
  const leaveOk = async () => !editing || !dirty || confirm({ title: 'Leave without saving?', message: "Your changes to this email aren't saved.", confirmLabel: 'Leave without saving', cancelLabel: 'Keep editing', danger: true });
  const pick = async id => { if (id !== picked && await leaveOk()) { setEditing(false); setDirty(false); setPicked(id); } };
  const pickVariant = async id => { if (id !== variant && await leaveOk()) { setEditing(false); setDirty(false); setVariant(id); } };

  async function sendTest() {
    setSending(true);
    try {
      const r = await call(`/${encodeURIComponent(current.id)}/test`, getToken, { method: 'POST', body: JSON.stringify({ variant }) });
      notify({ tone: 'success', title: 'Test email sent', detail: `Check ${r.sentTo}. The subject starts with [Test].` });
    } catch (e) { notify({ tone: 'error', title: "Couldn't send the test", detail: e.message }); }
    setSending(false);
  }

  async function afterChange() {
    setEditing(false); setDirty(false);
    try { await loadList(); } catch { /* the list reloads next time */ }
  }

  const groups = useMemo(() => {
    const t = q.trim().toLowerCase();
    const shown = (list || []).filter(x => !t || [x.name, x.when, x.to, x.group].some(v => String(v || '').toLowerCase().includes(t)));
    const map = new Map();
    shown.forEach(x => { if (!map.has(x.group)) map.set(x.group, []); map.get(x.group).push(x); });
    return [...map.entries()];
  }, [list, q]);

  if (error) return <div role="alert" style={S.error}>{error}</div>;
  if (!list) return <Loader label="Loading email templates…" />;
  const editable = list.filter(t => t.editable).length;
  const switchMode = async m => { if (m !== mode && await leaveOk()) { setEditing(false); setDirty(false); setMode(m); } };

  return (
    <div>
      <style>{EDITOR_CSS}</style>
      <div style={S.top}>
        {/* 3.49.20: no second "Email Templates" title (the Console section already shows it) */}
        <div role="tablist" aria-label="Email Templates" style={S.modes}>
          <button role="tab" aria-selected={mode === 'system'} onClick={() => switchMode('system')} style={{ ...S.mode, ...(mode === 'system' ? S.modeOn : {}) }}>System emails ({list.length})</button>
          <button role="tab" aria-selected={mode === 'announcements'} onClick={() => switchMode('announcements')} style={{ ...S.mode, ...(mode === 'announcements' ? S.modeOn : {}) }}><Emo e="📣" /> Announcements</button>
        </div>
        {mode === 'system'
          ? <p style={S.sub}>Every email the fax system sends: when it goes out, who gets it and where it comes from. Pick one to see it with sample details or send yourself a test copy. {editable} of them can be edited; the two stats reports are built from live numbers.</p>
          : <p style={S.sub}>Write your own email and send it to chosen people: by client, by role, or one by one. It has the portal's look, and {'{placeholders}'} such as their name are filled in for each person.</p>}
      </div>
      {mode === 'announcements' ? <Announcements /> : (

      <div style={S.layout}>
        <nav style={S.nav} aria-label="Email templates">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search emails" aria-label="Search emails" style={S.search} />
          {groups.map(([g, items]) => (
            <div key={g} style={{ marginBottom: 10 }}>
              <div style={S.groupTitle} role="heading" aria-level={5}><span>{g}</span><span style={S.groupCount}>{items.length}</span></div>
              {items.map(t => (
                <button key={t.id} onClick={() => pick(t.id)} aria-current={picked === t.id ? 'true' : undefined}
                  style={{ ...S.item, ...(picked === t.id ? S.itemOn : {}) }}>
                  <span style={S.itemName}>{t.name}{t.customized && <span style={S.editedPill}>Edited</span>}</span>
                  <span style={S.itemTo}>To: {t.to}</span>
                </button>
              ))}
            </div>
          ))}
          {!groups.length && <div style={S.muted}>Nothing matches.</div>}
        </nav>

        {current && (
          <section style={S.detail} aria-label={current.name}>
            <h4 style={S.detailTitle}>{current.name}</h4>
            <dl style={S.facts}>
              <Fact label="When it's sent" value={current.when} wide />
              <Fact label="Sent to" value={current.to} />
              <Fact label="From" value={current.from} />
              <Fact label="Sent by" value={current.sentBy} />
              <Fact label="Times in the email" value={current.timeZone} />
              <Fact label="Attachment" value={current.attachment ? 'Yes, the fax or report file' : 'No'} />
            </dl>
            {current.note && <p style={S.note}>{current.note}</p>}

            {current.hasPreview && (
              <>
                <div style={S.previewBar}>
                  {current.variants.length > 1 ? (
                    <div role="tablist" aria-label="Versions" style={S.variants}>
                      {current.variants.map(v => (
                        <button key={v.id} role="tab" aria-selected={variant === v.id} onClick={() => pickVariant(v.id)}
                          style={{ ...S.variant, ...(variant === v.id ? S.variantOn : {}) }}>{v.label}{v.customized ? ' ✎' : ''}</button>
                      ))}
                    </div>
                  ) : <span />}
                  {!editing && (
                    <div style={S.actions}>
                      {currentVariant?.editable && <button onClick={() => setEditing(true)} style={S.editBtn}><Emo e="✏️" /> Edit this email</button>}
                      <button onClick={sendTest} disabled={sending || !preview || preview.error} style={S.testBtn}>{sending ? 'Sending…' : emoLabel('✉️ Send test to me')}</button>
                    </div>
                  )}
                </div>
                {currentVariant?.customized && !editing && (
                  <p style={S.editedNote}>Edited by {currentVariant.editedBy} on {fmtWhen(currentVariant.editedAt)}. This is the version being sent.</p>
                )}
                {current.variants.length > 1 && currentVariant?.editable && !editing && (
                  <p style={S.hint}>Each version is edited separately.</p>
                )}

                {editing ? (
                  <Editor key={`${current.id}:${variant}`} id={current.id} variant={variant} getToken={getToken} notify={notify}
                    onDirty={setDirty} onDone={afterChange} onCancel={async () => { if (!dirty || await confirm({ title: 'Discard your changes?', message: "Your changes to this email aren't saved.", confirmLabel: 'Discard', cancelLabel: 'Keep editing', danger: true })) { setEditing(false); setDirty(false); } }} />
                ) : !preview ? <Loader size="sm" label="Loading preview…" />
                  : preview.error ? <div role="alert" style={S.error}>{preview.error}</div>
                  : <Preview subject={preview.subject} html={preview.html} title={`Preview: ${current.name}`} />}
              </>
            )}
          </section>
        )}
      </div>
      )}
    </div>
  );
}

function Fact({ label, value, wide }) {
  return (
    <div style={{ gridColumn: wide ? '1 / -1' : undefined, minWidth: 0 }}>
      <dt style={S.dt}>{label}</dt>
      <dd style={S.dd}>{value}</dd>
    </div>
  );
}

// ── The editor ──────────────────────────────────────────────
function Editor({ id, variant, getToken, notify, onDirty, onDone, onCancel }) {
  const { confirm } = useNotify();
  const [data, setData]       = useState(null);    // GET /:id/edit
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft]     = useState(null);
  const [loadKey, setLoadKey] = useState(0);        // remounts the rich-text boxes
  const [live, setLive]       = useState(null);     // draft preview { subject, html, problems }
  const [busy, setBusy]       = useState('');       // 'save' | 'test' | 'reset'
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const path = `/${encodeURIComponent(id)}`;
  const qs = `?variant=${encodeURIComponent(variant || 'default')}`;

  useEffect(() => {
    call(`${path}/edit${qs}`, getToken).then(r => { setData(r); setDraft(r.current); }).catch(e => setLoadError(e.message));
  }, [path, qs, getToken]);

  const phMap = useMemo(() => Object.fromEntries((data?.placeholders || []).map(p => [p.key, p])), [data]);
  const dirty = !!data && !!draft && !same(draft, data.current);
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);

  // Live preview, a moment after typing stops
  useEffect(() => {
    if (!draft) return undefined;
    const t = setTimeout(() => {
      call(`${path}/render`, getToken, { method: 'POST', body: JSON.stringify({ variant, fields: draft }) })
        .then(setLive).catch(e => setLive({ subject: '', html: '', problems: [e.message] }));
    }, 500);
    return () => clearTimeout(t);
  }, [draft, path, variant, getToken]);

  const setField = (name, value) => setDraft(d => ({ ...d, [name]: value }));
  const load = fields => { setDraft({ ...fields }); setLoadKey(k => k + 1); };
  const target = usePlaceholderTarget(draft || {}, setField);

  async function save() {
    setBusy('save');
    try {
      await call(path, getToken, { method: 'PUT', body: JSON.stringify({ variant, fields: draft }) });
      notify({ tone: 'success', title: 'Email saved', detail: 'From now on this email is sent with your text.' });
      onDone();
    } catch (e) { notify({ tone: 'error', title: "Couldn't save", detail: e.message }); }
    setBusy('');
  }
  async function sendDraft() {
    setBusy('test');
    try {
      const r = await call(`${path}/test`, getToken, { method: 'POST', body: JSON.stringify({ variant, fields: draft }) });
      notify({ tone: 'success', title: 'Test of your draft sent', detail: `Check ${r.sentTo}. Nothing is saved yet.` });
    } catch (e) { notify({ tone: 'error', title: "Couldn't send the test", detail: e.message }); }
    setBusy('');
  }
  async function reset() {
    if (!(await confirm({ title: 'Go back to the original text?', message: 'This email is sent with the original text again. Your edited version stays in History.', confirmLabel: 'Use the original' }))) return;
    setBusy('reset');
    try {
      await call(`${path}${qs}`, getToken, { method: 'DELETE' });
      notify({ tone: 'success', title: 'Back to the original', detail: 'This email is sent with the original text again.' });
      onDone();
    } catch (e) { notify({ tone: 'error', title: "Couldn't reset", detail: e.message }); }
    setBusy('');
  }
  async function openHistory() {
    setShowHistory(s => !s);
    if (history) return;
    try { setHistory((await call(`${path}/history${qs}`, getToken)).data); }
    catch (e) { setHistory([]); notify({ tone: 'error', title: "Couldn't load the history", detail: e.message }); }
  }

  if (loadError) return <div role="alert" style={S.error}>{loadError}</div>;
  if (!data || !draft) return <Loader label="Loading the editor…" />;
  const problems = live?.problems || [];
  const portal = data.family === 'portal';

  return (
    <div style={S.editor}>
      <div style={S.editCol}>
        <label style={S.label} htmlFor="et-subject">Subject</label>
        <input id="et-subject" value={draft.subject} onChange={e => setField('subject', e.target.value)} style={S.input} {...target.inputProps('subject')} />
        <label style={S.label} htmlFor="et-heading">Heading</label>
        <input id="et-heading" value={draft.heading} onChange={e => setField('heading', e.target.value)} style={S.input} {...target.inputProps('heading')} />
        <span style={S.label} id="et-body-label">Body</span>
        <RichText key={`body-${loadKey}`} labelId="et-body-label" initial={draft.body} phMap={phMap} minHeight={260}
          onChange={v => setField('body', v)} {...target.richProps} registerInsert={target.registerBody} />
        {portal && data.hasButton && (
          <>
            <label style={S.label} htmlFor="et-button">Button label</label>
            <input id="et-button" value={draft.button} onChange={e => setField('button', e.target.value)} style={S.input} {...target.inputProps('button')} />
            <p style={S.hint}>The button's link is set by the system (and a copy-paste link is added under it).</p>
          </>
        )}
        {portal && (
          <>
            <span style={S.label} id="et-small-label">Small print (under the button)</span>
            <RichText key={`small-${loadKey}`} labelId="et-small-label" initial={draft.smallPrint} phMap={phMap} minHeight={70}
              onChange={v => setField('smallPrint', v)} {...target.richProps} />
            <p style={S.hint}>Each paragraph shows in small grey text.</p>
          </>
        )}

        <PlaceholderPicker placeholders={data.placeholders} onPick={target.insert}
          note={`Purple ones are smart phrases: text the system writes (e.g. an explanation or a table), and leaves out when it doesn't apply.${data.placeholders.some(p => p.required) ? ' * must stay in the email.' : ''}`} />

        {problems.length > 0 && (
          <div role="alert" style={S.problems}>{problems.map(p => <div key={p}><Emo e="⚠️" /> {p}</div>)}</div>
        )}

        <div style={S.editActions}>
          <button onClick={save} disabled={!!busy || !dirty || problems.length > 0} style={S.saveBtn}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
          <button onClick={onCancel} disabled={!!busy} style={S.ghostBtn}>Cancel</button>
          <button onClick={sendDraft} disabled={!!busy || problems.length > 0} style={S.ghostBtn}>{busy === 'test' ? 'Sending…' : <><Emo e="✉️" /> Send test of this draft</>}</button>
          <span style={{ flex: 1 }} />
          {!same(draft, data.original) && <button onClick={() => load(data.original)} disabled={!!busy} style={S.linkBtn}>Start from the original text</button>}
          <button onClick={openHistory} disabled={!!busy} style={S.linkBtn}>History</button>
          {data.customized && <button onClick={reset} disabled={!!busy} style={S.dangerLink}>{busy === 'reset' ? 'Resetting…' : 'Back to the original'}</button>}
        </div>

        {showHistory && (
          <div style={S.history}>
            {!history ? <Loader size="sm" />
              : !history.length ? <div style={S.muted}>No changes yet. This email has always used the original text.</div>
              : history.map(h => (
                <div key={h.id} style={S.histRow}>
                  <span>{h.action === 'reset' ? 'Set back to the original' : 'Saved'} by {h.by} · {fmtWhen(h.at)}</span>
                  {h.fields && <button onClick={() => load(h.fields)} style={S.linkBtn}>Load into the editor</button>}
                </div>
              ))}
          </div>
        )}
      </div>

      <div style={S.previewCol}>
        <div style={S.liveTitle}>Preview with sample details {dirty && <span style={S.unsaved}>Not saved</span>}</div>
        {!live ? <div style={S.muted}>Building the preview…</div>
          : live.html ? <Preview subject={live.subject} html={live.html} title="Preview of your draft" height={700} />
          : <div style={S.muted}>Fix the problems above to see the preview.</div>}
      </div>
    </div>
  );
}

const S = {
  top: { marginBottom: 12 },
  title: { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  sub: { margin: 0, color: 'var(--t-607d8b)', fontSize: 13, maxWidth: 760, lineHeight: 1.5 },
  modes: { display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 8px' },
  mode: PILL,
  modeOn: PILL_ON,
  muted: { color: 'var(--t-90a4ae)', fontSize: 13, padding: 12 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  layout: { display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' },
  nav: { flex: '1 1 260px', maxWidth: 340, background: 'var(--c-ffffff)', borderRadius: 12, padding: 12, border: '1px solid var(--b-e8f5e9)', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', maxHeight: '75vh', overflowY: 'auto' },
  search: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', marginBottom: 10 },
  // Section headers look nothing like the templates under them (3.9.1)
  groupTitle: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 800, color: 'var(--t-ffffff)', background: 'var(--c-00695c)', textTransform: 'uppercase', letterSpacing: '0.6px', margin: '14px 0 6px', padding: '6px 10px', borderRadius: 6 },
  groupCount: { fontSize: 10, fontWeight: 700, background: 'rgb(var(--c-ffffff-rgb) / 0.22)', borderRadius: 10, padding: '1px 7px' },
  item: { display: 'flex', flexDirection: 'column', gap: 2, width: '100%', textAlign: 'left', background: 'none', border: '1px solid transparent', borderRadius: 8, padding: '7px 9px', cursor: 'pointer', fontFamily: 'inherit' },
  itemOn: { background: 'var(--c-e0f2f1)', border: '1px solid var(--c-80cbc4)' },
  itemName: { fontSize: 13, fontWeight: 600, color: 'var(--t-263238)' },
  itemTo: { fontSize: 11, color: 'var(--t-78909c)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  editedPill: { marginLeft: 6, padding: '1px 7px', borderRadius: 10, background: 'var(--c-fff3e0)', color: 'var(--t-e65100)', fontSize: 10, fontWeight: 700, verticalAlign: 'middle' },
  detail: { flex: '3 1 480px', minWidth: 0, background: 'var(--c-ffffff)', borderRadius: 12, padding: '16px 18px', border: '1px solid var(--b-e8f5e9)', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)' },
  detailTitle: { margin: '0 0 10px', fontSize: 17, color: 'var(--t-263238)' },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px 18px', margin: '0 0 12px' },
  dt: { fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  dd: { margin: '2px 0 0', fontSize: 13, color: 'var(--t-37474f)', lineHeight: 1.45, overflowWrap: 'anywhere' },
  note: { background: 'var(--c-fff8e1)', color: 'var(--t-5d4037)', borderRadius: 8, padding: '9px 12px', fontSize: 13, margin: '0 0 12px' },
  previewBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '4px 0 10px' },
  variants: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  variant: { padding: '5px 12px', borderRadius: 16, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  variantOn: { background: 'var(--c-00695c)', border: '1.5px solid var(--c-00695c)', color: 'var(--t-ffffff)' },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  editBtn: { padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--c-00897b)', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  testBtn: { padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  editedNote: { background: 'var(--c-fff3e0)', color: 'var(--t-8d4a00)', borderRadius: 8, padding: '8px 12px', fontSize: 13, margin: '0 0 10px' },
  hint: { margin: '4px 0 8px', color: 'var(--t-90a4ae)', fontSize: 12, lineHeight: 1.45 },

  editor: { display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' },
  editCol: { flex: '1 1 420px', minWidth: 0 },
  previewCol: { flex: '1 1 380px', minWidth: 0, position: 'sticky', top: 8 },
  label: { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '10px 0 4px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, fontFamily: 'inherit', marginBottom: 2 },
  problems: { marginTop: 10, background: 'var(--c-fff3e0)', color: 'var(--t-8d4a00)', borderRadius: 8, padding: '8px 12px', fontSize: 13, lineHeight: 1.6 },
  editActions: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 },
  saveBtn: { padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  ghostBtn: { padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-455a64)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  linkBtn: { padding: '4px 6px', border: 'none', background: 'none', color: 'var(--t-00897b)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' },
  dangerLink: { padding: '4px 6px', border: 'none', background: 'none', color: 'var(--t-c62828)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' },
  history: { marginTop: 10, border: '1px solid var(--c-eceff1)', borderRadius: 10, padding: '6px 10px', maxHeight: 220, overflowY: 'auto' },
  histRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--c-f5f5f5)', fontSize: 13, color: 'var(--t-455a64)', flexWrap: 'wrap' },
  liveTitle: { fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '10px 0 6px', display: 'flex', gap: 8, alignItems: 'center' },
  unsaved: { padding: '1px 8px', borderRadius: 10, background: 'var(--c-fff3e0)', color: 'var(--t-e65100)', fontSize: 10, textTransform: 'none', letterSpacing: 0 },
};
